import moment from 'moment';

import {DiagnosticStatus as DiagnosticStatusEnum} from 'gql/types';

import type {TeacherDiagnosticListFieldsFragment} from '../queries/TeacherDiagnostics.queries.generated';

export type DiagnosticStatusKind = 'open' | 'scheduled' | 'closed';

export interface DiagnosticStatus {
  kind: DiagnosticStatusKind;
  /** Earliest relevant date (min due for open/closed, earliest future start for scheduled). */
  from: string | null;
  /** Latest relevant date; null when it equals `from` (single date, not a range). */
  to: string | null;
}

type Settings = TeacherDiagnosticListFieldsFragment['diagnosticSettings'];

const KIND_BY_ENUM: Record<DiagnosticStatusEnum, DiagnosticStatusKind> = {
  [DiagnosticStatusEnum.Open]: 'open',
  [DiagnosticStatusEnum.Scheduled]: 'scheduled',
  [DiagnosticStatusEnum.Closed]: 'closed'
};

const range = (dates: string[]): {from: string | null; to: string | null} => {
  if (dates.length === 0) return {from: null, to: null};
  const sorted = [...dates].sort();
  const from = sorted[0];
  const to = sorted[sorted.length - 1];
  return {from, to: to === from ? null : to};
};

// Scheduled groups are exactly those whose start_date is still in the future.
const futureStartDates = (settings: Settings, now: Date): string[] =>
  settings.map((s) => s.startDate).filter((d): d is string => Boolean(d) && new Date(d) > now);

export const diagnosticStatus = (
  diagnostic: TeacherDiagnosticListFieldsFragment,
  now: Date = new Date()
): DiagnosticStatus => {
  const kind = diagnostic.status ? KIND_BY_ENUM[diagnostic.status] : 'open';
  // Open and closed both display the due-date range; scheduled shows the start range.
  const dueDates = [diagnostic.minDueDate, diagnostic.maxDueDate].filter(Boolean) as string[];
  switch (kind) {
    case 'scheduled':
      return {kind, ...range(futureStartDates(diagnostic.diagnosticSettings, now))};
    case 'open':
    case 'closed':
      return {kind, ...range(dueDates)};
  }
};

const formatDateTime = (iso: string): string => moment(iso).format('MMM D, h:mm A');
// Ranges drop the time for brevity, e.g. "Oct 24 - Nov 1".
const formatDateShort = (iso: string): string => moment(iso).format('MMM D');

const dueLabel = (from: string, to: string | null): string =>
  to ? `Due from ${formatDateShort(from)} - ${formatDateShort(to)}` : `Due ${formatDateTime(from)}`;

export const formatStatusLabel = ({kind, from, to}: DiagnosticStatus): string => {
  switch (kind) {
    case 'open':
      return from ? dueLabel(from, to) : 'Open';
    case 'scheduled':
      if (!from) return 'Scheduled';
      return to
        ? `Scheduled for ${formatDateShort(from)} - ${formatDateShort(to)}`
        : `Scheduled for ${formatDateTime(from)}`;
    case 'closed':
      return from ? `Closed, ${dueLabel(from, to)}` : 'Closed';
  }
};
